import { before, after } from './bits'

import ERD from 'element-resize-detector'

import idlize from './idlize'
import envRaw from './env-raw'
import Emitter from './emitter'
import StrictObj from './strict-obj'
import MapSimple from './map-simple'
import forceResize from './force-resize'
import debugFields from './debug-fields'
import extractRawElm from './extract-raw-elm'
import viewportResize from './viewport-resize'

import { getElmStr, getElmStrShort } from './get-elm-str'

export const cache = MapSimple()

function erd() {
  if (erd.__val__) {
    return erd.__val__
  } else {
    return erd.__val__ = envRaw.useNativeResizeObserver
      ? NativeRD()
      : FallbackRD()
  }
}

export default function ResizeDetector($elm) {
  const elm = extractRawElm($elm)

  if (cache.has(elm)) {
    return cache.get(elm)
  }

  let destroyed = false
  let lastWidth = null
  let lastHeight = null
  let ignoresFRE = false
  const detector = cache.set(elm, Emitter(`resizeDetector[elm=${getElmStrShort(elm)}]`))

  let intervalId = null
  const intervalCheckDelay = 1000 // ms
  const idlizedFallbackHandler = idlize(fallbackHandler)

  detector.on = before(beforeOn, detector.on)
  detector.pre = before(beforeOn, detector.pre)
  detector.after = before(beforeOn, detector.after)
  detector.off = after(detector.off, afterOff)
  detector.clear = after(detector.clear, afterOff)

  detector.getWidth = getWidth
  detector.getHeight = getHeight

  detector.manualTrigger = manualTrigger
  detector.ignoreForceResizeEvents = ignoreForceResizeEvents

  return debugFields(detector, {
    _elm: elm,
    _lastSize: lastSize,
    _destroyed: () => destroyed,
    _mainHandler: mainHandler,
    _fallbackHandler: fallbackHandler,
    _getActualWidth: getActualWidth,
    _getActualHeight: getActualHeight
  })

  function ignoreForceResizeEvents() {
    ignoresFRE = true
    return detector
  }

  function lastSize() {
    return {
      lastWidth,
      lastHeight
    }
  }

  function getWidth() {
    return lastWidth !== null ? lastWidth : getActualWidth()
  }

  function getHeight() {
    return lastHeight !== null ? lastHeight : getActualHeight()
  }

  function getActualWidth() {
    return erd().getWidth(elm)
  }

  function getActualHeight() {
    return erd().getHeight(elm)
  }

  function manualTrigger() {
    lastWidth = null
    lastHeight = null
    if (erd().has(elm)) {
      erd().manualTrigger(elm)
    }
  }

  function fallbackHandler() {
    mainHandler(getActualWidth(), getActualHeight())
  }

  function forceResizeHandler() {
    if (!ignoresFRE) {
      manualTrigger()
    }
  }

  function mainHandler(width, height) {
    const changed = (width !== lastWidth) || (height !== lastHeight)
    if (changed) {
      lastWidth = width
      lastHeight = height
      detector.emit(lastWidth, lastHeight, elm)
    }
  }

  function beforeOn() {
    if (destroyed) {
      throw new Error(`ResizeDetector: already destroyed! ${getElmStr(elm)}`)
    }

    if (detector.size() === 0) {
      erd().observe(elm, mainHandler)
      forceResize.emitter.on(forceResizeHandler)
      if (!erd().native) {
        viewportResize.on(idlizedFallbackHandler)
        intervalId = setInterval(idlizedFallbackHandler, intervalCheckDelay)
      }
    }
  }

  function afterOff() {
    if (!destroyed && detector.size() === 0) {
      erd().unobserve(elm)
      forceResize.emitter.off(forceResizeHandler)
      viewportResize.off(idlizedFallbackHandler)
      clearInterval(intervalId)
      cache.del(elm)
      destroyed = true
    }
  }
}

function FallbackRD() {
  const frd = ERD({ strategy: 'scroll', important: true })
  const elms = []
  const widths = []
  const heights = []
  const callbacks = []
  const serviceClass = 'erd_scroll_detection_container'

  return StrictObj(debugFields({
    native: false,
    observe,
    unobserve,
    has,
    getWidth,
    getHeight,
    manualTrigger
  }, {
    _frd: frd,
    _elms: elms,
    _widths: widths,
    _heights: heights,
    _handler: handler,
    _callbacks: callbacks,
    _serviceClass: serviceClass
  }))

  function has(elm) {
    if (elm.classList.contains(serviceClass)) {
      return true
    }

    const elmId = elms.indexOf(elm)
    return elmId !== -1
  }

  function getWidth(elm) {
    return Math.round(elm.offsetWidth)
  }

  function getHeight(elm) {
    return Math.round(elm.offsetHeight)
  }

  function manualTrigger(elm) {
    if (elm.classList.contains(serviceClass)) {
      return
    }

    const elmId = elms.indexOf(elm)
    if (elmId !== -1) {
      const width = getWidth(elm)
      const height = getHeight(elm)
      const callback = callbacks[elmId]
      widths[elmId] = width
      heights[elmId] = height
      callback(width, height)
    }
  }

  function handler(elm) {
    const elmId = elms.indexOf(elm)
    if (elmId !== -1) {
      const width = getWidth(elm)
      const height = getHeight(elm)
      const callback = callbacks[elmId]
      widths[elmId] = width
      heights[elmId] = height
      callback(width, height)
    }
  }

  function observe(elm, callback) {
    if (elm.classList.contains(serviceClass)) {
      return
    }

    elms.push(elm)
    widths.push(null)
    heights.push(null)
    callbacks.push(callback)
    frd.listenTo(elm, handler)
  }

  function unobserve(elm) {
    if (elm.classList.contains(serviceClass)) {
      return
    }

    frd.uninstall(elm)
    const elmId = elms.indexOf(elm)
    if (elmId !== -1) {
      elms.splice(elmId, 1)
      widths.splice(elmId, 1)
      heights.splice(elmId, 1)
      callbacks.splice(elmId, 1)
    }
  }
}

function NativeRD() {
  const nrd = new window.ResizeObserver(handler)
  const elms = []
  const init = []
  const iids = []
  const widths = []
  const heights = []
  const callbacks = []

  return StrictObj(debugFields({
    native: true,
    observe,
    unobserve,
    has,
    getWidth,
    getHeight,
    manualTrigger
  }, {
    _nrd: nrd,
    _elms: elms,
    _init: init,
    _iids: iids,
    _widths: widths,
    _heights: heights,
    _handler: handler,
    _callbacks: callbacks
  }))

  function has(elm) {
    const elmId = elms.indexOf(elm)
    return elmId !== -1
  }

  function getWidth(elm) {
    const rect = elm.getBoundingClientRect()
    return Math.round(rect.width)
  }

  function getHeight(elm) {
    const rect = elm.getBoundingClientRect()
    return Math.round(rect.height)
  }

  function manualTrigger(elm) {
    const elmId = elms.indexOf(elm)
    if (elmId !== -1) {
      const rect = elm.getBoundingClientRect()
      const width = Math.round(rect.width)
      const height = Math.round(rect.height)
      const callback = callbacks[elmId]
      widths[elmId] = width
      heights[elmId] = height
      clearTimeout(iids[elmId])
      callback(width, height)
    }
  }

  function handler(entries) {
    for (const entry of entries) {
      const elmId = elms.indexOf(entry.target)
      if (elmId !== -1) {
        if (init[elmId]) {
          const width = Math.round(entry.contentRect.width)
          const height = Math.round(entry.contentRect.height)
          const callback = callbacks[elmId]
          widths[elmId] = width
          heights[elmId] = height
          // this is necessary to avoid possible infinite loop
          // which may happen if resize handler will change elm size
          clearTimeout(iids[elmId])
          iids[elmId] = setTimeout(callback, 0, width, height)
        } else {
          // native ResizeObserver emits unwanted event after initialization
          init[elmId] = true
        }
      }
    }
  }

  function observe(elm, callback) {
    elms.push(elm)
    init.push(false)
    iids.push(null)
    widths.push(null)
    heights.push(null)
    callbacks.push(callback)
    nrd.observe(elm)
  }

  function unobserve(elm) {
    nrd.unobserve(elm)
    const elmId = elms.indexOf(elm)
    if (elmId !== -1) {
      elms.splice(elmId, 1)
      init.splice(elmId, 1)
      iids.splice(elmId, 1)
      widths.splice(elmId, 1)
      heights.splice(elmId, 1)
      callbacks.splice(elmId, 1)
    }
  }
}
